import { AdBanner, useBannerInPlay } from "../ads/AdBanner.js";
import { adsForMe, loadAds } from "../ads/ads.js";
import { InviteFriends } from "./FriendInvites.js";
import { startBlocker, teamsBlocker } from "./start-blocker.js";
import { useSignal } from "@preact/signals";
import { useEffect, useMemo, useRef } from "preact/hooks";
import games from "virtual:obean-games";
import { GameHost } from "../host/host.js";
import { GameUnavailable, loadGame, reloadOnce } from "../host/loader.js";
import type { AnyGame, GameRef } from "../host/loader.js";
import { OnlineDriver } from "../host/online-driver.js";
import { setSoundOn, soundOn } from "../host/sound-pref.js";
import { MicIcon, SettingsIcon, SoundIcon, VoiceIcon } from "../ui/icons.js";
import { HourNote, openSettings } from "../settings/SettingsSheet.js";
import { StarsMoment } from "../moments/moments.js";
import { track } from "../host/telemetry.js";
import { tipFor, unlockLine } from "../solo/next-step.js";
import type { Earned } from "../loop/beans.js";
import { award, beansOf, levelOf, loadWallet, wonBy } from "../loop/beans.js";
import { BeansEarned } from "../loop/BeansEarned.js";
import { keepFeats, keepWallet, profileId } from "../loop/profile.js";
import type { Feat } from "../loop/feats.js";
import { FeatsEarned } from "../loop/FeatsEarned.js";
import { PlayerInput, TouchControls, keyboardHint } from "../input/index.js";
import { TeachCard, teachingNow, useTeach } from "../teach/TeachCard.js";
import { rememberLastPlay } from "../home/home.js";
import type { Hosting, Invite } from "../net/api.js";
import { VoiceSession } from "../voice/session.js";
import { GameCards } from "./GameCards.js";
import { HostingLimit, PassMissions, hostingOf } from "./PassCards.js";
import { lockedPassMissions, passReturn, untilOwned } from "./pass.js";
import { ApiError, NICKNAME_ERRORS, ON_HOLD, TOO_MANY_TRIES, api } from "../net/api.js";
import { usePlaying } from "../net/presence.js";
import { FeedbackPrompt } from "./Feedback.js";
import type { JoinStage } from "../net/join.js";
import { inviteProblem, joinFailure, secretFromHash } from "../net/join.js";
import type { ReportCategory, SnapshotFrame } from "@obean/protocol";
import {
  MAX_CHAT_CHARS,
  MAX_REPORT_NOTE_CHARS,
  QUICK_MESSAGES,
  REACTIONS,
  REPORT_CATEGORIES,
  chatKind,
} from "@obean/protocol";
import type { MatchLoad, RoomMoved, RoomState } from "../net/room-connection.js";
import { RoomConnection, canRejoin, closedMessage } from "../net/room-connection.js";
import { navigate } from "../router.js";
import { INVITE_QR } from "../share/invite-qr.js";
import { cardFile, dailyShareLine, headlineOf, shareText } from "../share/result-card.js";
import {
  recallInvite,
  rememberFollow,
  rememberInvite,
  rememberLeft,
  rememberRoomGame,
  takeFollow,
  takeRoomGame,
} from "./invite-memory.js";
import { EXTRAS, SEAT_COLOURS, extraLevel } from "@obean/beans";
import { Bean, moodOf } from "../ui/Bean.js";
import { Stars } from "../ui/Stars.js";
import { MissionMap } from "../map/MissionMap.js";
import { dailyNumber, dailyOf, dayOf } from "@obean/game-contract";
import { recordDaily, shareDaily } from "../solo/daily.js";
import { diagMark } from "../host/diagnostics.js";
import { TellUs } from "./TellUs.js";

/** How often a page in the background reminds the room while a match is played (P2-WEB-06). */
const AWAY_REPORT_MS = 5_000;

/**
 * Share an invite link: the phone's share sheet when there is one, else copy; or show it as a QR
 * code for a friend in the same room to scan with their camera (invite_shared).
 */
function InviteCard({ invite }: { invite: Invite }) {
  const done = useSignal<string | null>(null);
  const canShare = typeof navigator.share === "function";
  const showQr = useSignal(false);
  const qr = useSignal<{ url: string; svg: string } | null>(null);
  useEffect(() => {
    if (!showQr.value || qr.value?.url === invite.url) return;
    let current = true;
    void import("uqr").then(({ renderSVG }) => {
      if (current) qr.value = { url: invite.url, svg: renderSVG(invite.url, INVITE_QR) };
    });
    return () => {
      current = false;
    };
  }, [showQr.value, invite.url]);
  const toggleQr = () => {
    showQr.value = !showQr.value;
    if (showQr.value) track("invite_shared", { method: "qr" });
  };
  const share = async () => {
    try {
      if (canShare) {
        await navigator.share({ title: "Obean", text: "Join me on Obean", url: invite.url });
        track("invite_shared", { method: "native_share" });
        done.value = "Shared.";
      } else {
        await navigator.clipboard.writeText(invite.url);
        track("invite_shared", { method: "copy" });
        done.value = "Link copied.";
      }
    } catch {
      // The share sheet was dismissed, or the clipboard is off: nothing shared.
    }
  };
  return (
    <div class="card invite">
      <h2>Invite a friend</h2>
      <input
        readOnly
        value={invite.url}
        aria-label="Invite link"
        onFocus={(e) => {
          e.currentTarget.select();
        }}
      />
      <div class="invite-actions">
        <button type="button" onClick={() => void share()}>
          {canShare ? "Share link" : "Copy link"}
        </button>
        <button
          type="button"
          class="secondary"
          aria-expanded={showQr.value}
          aria-controls="invite-qr"
          onClick={toggleQr}
        >
          {showQr.value ? "Hide QR code" : "Show QR code"}
        </button>
      </div>
      {showQr.value && (
        <figure class="invite-qr" id="invite-qr">
          {qr.value?.url === invite.url && (
            <div
              class="qr-code"
              role="img"
              aria-label="QR code of the invite link"
              // uqr's own SVG markup, made from the link: nothing from other players.
              dangerouslySetInnerHTML={{ __html: qr.value.svg }}
            />
          )}
          <figcaption>Sitting together? Your friend points their phone's camera here.</figcaption>
        </figure>
      )}
      <p class="hint" aria-live="polite">
        {done.value ?? "The link works for 24 hours."}
      </p>
    </div>
  );
}

/** A finished match's card, and what to say with it (P2-WEB-07). */
interface ResultCard {
  file: File;
  text: string;
  props: Record<string, unknown>;
}

/**
 * Share the result (P2-WEB-07): the phone's share sheet with the card image where it takes files,
 * else the card is saved (result_card_shared). The card was made before the tap, so the share sheet
 * opens inside the gesture, as Safari requires.
 */
/**
 * Right after a match (ADR 0031): signed-in teammates from this room you aren't friends with yet.
 * Guests and people already friends see nothing.
 */
function AddFriends({ roomId }: { roomId: string }) {
  const people = useSignal<{ id: string; name: string; asked: boolean }[]>([]);
  useEffect(() => {
    api.friendsInRoom(roomId).then(
      (r) => {
        people.value = r.people;
      },
      () => {
        // Not signed in, or offline: nothing to offer.
      },
    );
  }, [roomId]);
  if (people.value.length === 0) return null;
  return (
    <div class="add-friends">
      {people.value.map((p) =>
        p.asked ? (
          <p key={p.id} class="hint">
            Asked {p.name} to be friends
          </p>
        ) : (
          <button
            key={p.id}
            type="button"
            class="secondary"
            onClick={() => {
              api.requestFriend(p.id, "match").then(
                () => {
                  people.value = people.value.map((q) =>
                    q.id === p.id ? { ...q, asked: true } : q,
                  );
                },
                () => undefined,
              );
            }}
          >
            Add {p.name} as a friend
          </button>
        ),
      )}
    </div>
  );
}

function ShareResult({ card }: { card: ResultCard }) {
  const note = useSignal<string | null>(null);
  const share = async () => {
    const files = [card.file];
    try {
      if (typeof navigator.canShare === "function" && navigator.canShare({ files })) {
        await navigator.share({ files, text: card.text, title: "Obean" });
        track("result_card_shared", { ...card.props, method: "native_share" });
        note.value = "Shared.";
        return;
      }
      const url = URL.createObjectURL(card.file);
      const link = document.createElement("a");
      link.href = url;
      link.download = card.file.name;
      link.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10_000);
      track("result_card_shared", { ...card.props, method: "download" });
      note.value = "Card saved.";
    } catch {
      // The share sheet was dismissed: nothing shared.
    }
  };
  return (
    <>
      <button type="button" class="secondary" onClick={() => void share()}>
        Share result
      </button>
      {note.value && (
        <p class="share-note" role="status">
          {note.value}
        </p>
      )}
    </>
  );
}

/** The sender's bean beside a chat line (ADR 0018); nothing for someone who has left. */
function ChatBean({ room, seat }: { room: RoomState; seat: string }) {
  const i = room.members.findIndex((m) => m.seat === seat);
  const m = room.members[i];
  return m ? <Bean colour={m.colour ?? i} extra={m.bean ?? null} size={24} /> : null;
}

/**
 * Chat in the room (P2-WEB-03, P2-RT-09): what's been said since you joined, a line to type, and
 * one-tap quick messages and reactions (easier on a phone). Nothing is kept: the room relays and
 * forgets. Text is shown as text, so links aren't clickable.
 */
function ChatCard({ connection, room }: { connection: RoomConnection; room: RoomState }) {
  const draft = useSignal("");
  const list = useRef<HTMLDivElement>(null);
  const muted = connection.muted.value;
  const messages = connection.chat.value.filter((m) => !muted.has(m.from));
  const error = connection.error.value;
  const slowDown = error?.code === "rate_limited" && error.about === "chat.send";
  const online = connection.status.value.kind === "connected";
  const me = connection.seat.value;
  const nameOf = (seat: string) =>
    room.members.find((m) => m.seat === seat)?.name ?? "Someone who left";
  const send = (text: string) => {
    const said = text.trim();
    if (!said || !online) return;
    connection.error.value = null;
    connection.send({ type: "chat.send", text: said });
  };
  useEffect(() => {
    list.current?.lastElementChild?.scrollIntoView({ block: "nearest" });
  }, [messages.length]);
  return (
    <section class="card chat" aria-label="Chat">
      {/* A log of messages (role "log", read as they arrive), not a list: axe, 2026-10-08. */}
      <div class="messages" role="log" aria-live="polite" ref={list}>
        {messages.length === 0 && <p class="empty">Say hi, or tap a quick message.</p>}
        {messages.map((m) => (
          <p
            key={m.id}
            class={`message ${m.from === me ? "mine" : ""} ${chatKind(m.text) === "reaction" ? "reaction" : ""}`}
          >
            <ChatBean room={room} seat={m.from} />
            <span class="who">{m.from === me ? "You" : nameOf(m.from)}</span>
            <span class="text">{m.text}</span>
          </p>
        ))}
      </div>
      <div class="quick" role="group" aria-label="Quick messages">
        {QUICK_MESSAGES.map((q) => (
          <button
            type="button"
            key={q}
            disabled={!online}
            onClick={() => {
              send(q);
            }}
          >
            {q}
          </button>
        ))}
      </div>
      <div class="quick reactions" role="group" aria-label="Reactions">
        {REACTIONS.map((r) => (
          <button
            type="button"
            key={r}
            class="reaction"
            aria-label={`React ${r}`}
            disabled={!online}
            onClick={() => {
              send(r);
            }}
          >
            {r}
          </button>
        ))}
      </div>
      <form
        class="say"
        onSubmit={(e) => {
          e.preventDefault();
          send(draft.value);
          draft.value = "";
        }}
      >
        <input
          aria-label="Message"
          placeholder="Message"
          autocomplete="off"
          enterkeyhint="send"
          maxLength={MAX_CHAT_CHARS}
          value={draft.value}
          onInput={(e) => {
            draft.value = e.currentTarget.value;
          }}
        />
        <button type="submit" disabled={!online || draft.value.trim() === ""}>
          Send
        </button>
      </form>
      {slowDown && (
        <p class="hint" role="status">
          Slow down a little: 5 messages per 10 seconds.
        </p>
      )}
    </section>
  );
}

/** How the client words the protocol's report categories. */
const REPORT_REASONS: Record<ReportCategory, string> = {
  harassment: "Harassment or hate",
  cheating: "Cheating",
  name: "Offensive name",
  spam: "Spam",
  other: "Something else",
};

type Member = RoomState["members"][number];

/**
 * Reporting a player (P2-SAFE-01): what happened, and an optional note. The room adds its recent
 * chat as context.
 */
function ReportDialog(props: {
  connection: RoomConnection;
  member: Member;
  onClose: (sent: boolean) => void;
}) {
  const { connection, member } = props;
  const dialog = useRef<HTMLDialogElement>(null);
  const category = useSignal<ReportCategory | null>(null);
  const note = useSignal("");
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  const send = () => {
    if (!category.value) return;
    // The protocol takes one line of text: line breaks become spaces.
    const said = note.value.replace(/\s+/g, " ").trim();
    connection.send({
      type: "room.report",
      seat: member.seat,
      category: category.value,
      ...(said ? { note: said } : {}),
    });
    props.onClose(true);
  };
  return (
    <dialog
      ref={dialog}
      class="report"
      aria-labelledby="report-title"
      onClose={() => {
        props.onClose(false);
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <h2 id="report-title">Report {member.name}</h2>
        <fieldset>
          <legend>What happened?</legend>
          {REPORT_CATEGORIES.map((c) => (
            <label key={c}>
              <input
                type="radio"
                name="category"
                value={c}
                checked={category.value === c}
                onChange={() => {
                  category.value = c;
                }}
              />
              {REPORT_REASONS[c]}
            </label>
          ))}
        </fieldset>
        <label for="report-note">Anything to add? (optional)</label>
        <textarea
          id="report-note"
          rows={3}
          maxLength={MAX_REPORT_NOTE_CHARS}
          value={note.value}
          onInput={(e) => {
            note.value = e.currentTarget.value;
          }}
        />
        <p class="hint">The room's recent chat goes with your report.</p>
        <div class="actions">
          <button type="submit" disabled={category.value === null}>
            Send report
          </button>
          <button
            type="button"
            class="secondary"
            onClick={() => {
              props.onClose(false);
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * What you can do about another player (P2-SAFE-01, docs/safety-and-privacy.md): mute them here,
 * block them (no rooms together again), report them, or, as host, remove them from the room.
 */
function MemberActions(props: { connection: RoomConnection; member: Member; isHost: boolean }) {
  const { connection, member } = props;
  const open = useSignal(false);
  const confirming = useSignal<"block" | "remove" | null>(null);
  const reporting = useSignal(false);
  const notice = useSignal<string | null>(null);
  const muted = connection.muted.value.has(member.seat);
  const error = connection.error.value;
  const failed =
    error?.code === "unavailable" &&
    (error.about === "room.report" || error.about === "room.block");
  const done = (text: string) => {
    notice.value = text;
    confirming.value = null;
    open.value = false;
  };
  return (
    <>
      <button
        type="button"
        class="more"
        aria-expanded={open.value}
        aria-label={`Options for ${member.name}`}
        onClick={() => {
          open.value = !open.value;
          confirming.value = null;
        }}
      >
        ⋯
      </button>
      {open.value && (
        <div class="member-actions" role="group" aria-label={`${member.name}: options`}>
          {confirming.value === null && (
            <>
              <button
                type="button"
                onClick={() => {
                  connection.setMuted(member.seat, !muted);
                  done(muted ? `${member.name} unmuted.` : `${member.name}'s chat is hidden.`);
                }}
              >
                {muted ? "Unmute" : "Mute"}
              </button>
              <button
                type="button"
                onClick={() => {
                  confirming.value = "block";
                }}
              >
                Block…
              </button>
              <button
                type="button"
                onClick={() => {
                  reporting.value = true;
                  open.value = false;
                }}
              >
                Report…
              </button>
              {props.isHost && (
                <button
                  type="button"
                  onClick={() => {
                    confirming.value = "remove";
                  }}
                >
                  Remove from room…
                </button>
              )}
            </>
          )}
          {confirming.value === "block" && (
            <>
              <p>Block {member.name}? You won't be put in rooms together again.</p>
              <button
                type="button"
                onClick={() => {
                  connection.error.value = null;
                  connection.send({ type: "room.block", seat: member.seat });
                  connection.setMuted(member.seat, true);
                  done(`${member.name} is blocked.`);
                }}
              >
                Block
              </button>
            </>
          )}
          {confirming.value === "remove" && (
            <>
              <p>Remove {member.name} from the room? They can't come back to it.</p>
              <button
                type="button"
                onClick={() => {
                  connection.send({ type: "room.remove", seat: member.seat });
                  done(`${member.name} was removed.`);
                }}
              >
                Remove
              </button>
            </>
          )}
          {confirming.value !== null && (
            <button
              type="button"
              class="secondary"
              onClick={() => {
                confirming.value = null;
              }}
            >
              Cancel
            </button>
          )}
        </div>
      )}
      {(notice.value || failed) && (
        <p class="member-notice" role="status">
          {failed ? "That didn't go through. Try again in a moment." : notice.value}
        </p>
      )}
      {reporting.value && (
        <ReportDialog
          connection={connection}
          member={member}
          onClose={(sent) => {
            reporting.value = false;
            if (sent) {
              connection.error.value = null;
              notice.value = "Thanks: your report was sent.";
            }
          }}
        />
      )}
    </>
  );
}

/** The room's game's loadouts (e.g. the tank's roles), from the games built into this release. */
const loadoutsOf = (room: RoomState) => games.find((g) => g.id === room.game?.id)?.loadouts ?? [];
const missionsOf = (room: RoomState) => games.find((g) => g.id === room.game?.id)?.missions ?? [];
/** How many play the room's game (P4-PL-01); two when unknown. */
const playersOf = (room: RoomState) =>
  games.find((g) => g.id === room.game?.id)?.players ?? { min: 2, max: 2 };

/** The buttons the room's game declares (ADR 0017). */
const buttonsOf = (room: RoomState) => games.find((g) => g.id === room.game?.id)?.buttons ?? [];
/** How touch screens steer the room's game (ADR 0023). */
const touchOf = (room: RoomState) => games.find((g) => g.id === room.game?.id)?.touch ?? "stick";
/**
 * Whether the stage leaves room for touch controls beside it: a swipe or stick game's (ADR 0023,
 * the stick's dock), or a tap game's with buttons (ADR 0027).
 */
const barred = (touchUi: boolean, room: RoomState) =>
  touchUi && (touchOf(room) !== "tap" || buttonsOf(room).length > 0);
const missionName = (room: RoomState, id: string) =>
  missionsOf(room).find((m) => m.id === id)?.name ?? id;

/** The mission after the room's current one, if the room has reached it (a win opens it). */
function nextMission(room: RoomState): string | null {
  const reached = room.reached ?? [];
  const at = room.mission === undefined ? -1 : reached.indexOf(room.mission);
  return at >= 0 ? (reached[at + 1] ?? null) : null;
}

/**
 * Which mission the next match plays (P2-TANK-07). Rooms start at the first; a win opens the next.
 * The host picks any the room has reached; everyone sees the room's current one.
 */
/**
 * Which game the next match plays (second-game spike finding 1): the host picks between the games
 * in this build, between matches (`room.select`); everyone else sees the choice. Hidden while there's
 * only one game.
 */
function GamePicker({
  connection,
  room,
  isHost,
}: {
  connection: RoomConnection;
  room: RoomState;
  isHost: boolean;
}) {
  if (games.length < 2 || !room.game) return null;
  const current = games.find((g) => g.id === room.game?.id);
  if (!isHost)
    return (
      <div class="card games-pick">
        <p class="mission">
          Game: <strong>{current?.displayName ?? room.game.id}</strong> (the host picks)
        </p>
        {current && <img class="game-shot" src={current.art} alt="" width={320} height={180} />}
      </div>
    );
  const online = connection.status.value.kind === "connected";
  return (
    <fieldset class="card games-pick">
      <legend>Game</legend>
      <GameCards
        current={room.game.id}
        disabled={!online}
        onPick={(id) => {
          if (room.game?.id !== id) connection.send({ type: "room.select", game: id });
        }}
      />
    </fieldset>
  );
}

/**
 * The pair's best stars per mission (P3-TANK-04): from the api when the room is between matches or
 * someone joins, plus the match that just ended, whose record may land a moment after the room
 * does.
 */
function useBestStars(connection: RoomConnection, room: RoomState, roomId: string) {
  const best = useSignal<Record<string, number>>({});
  const game = room.game?.id;
  useEffect(() => {
    if (!game) return;
    let current = true;
    api.stars(roomId, game).then(
      ({ stars }) => {
        if (current) best.value = stars;
      },
      () => {
        // Stars are a nicety: without them the picker just shows names.
      },
    );
    return () => {
      current = false;
    };
  }, [game, room.phase, room.members.length, roomId]);
  const ended = connection.ended.value;
  const just = connection.match.value?.missionId;
  const won = ended?.result === "win" ? ended.stats["stars"] : undefined;
  if (just && won !== undefined && won > (best.value[just] ?? 0))
    return { ...best.value, [just]: won };
  return best.value;
}

function MissionPicker(props: {
  connection: RoomConnection;
  roomId: string;
  room: RoomState;
  isHost: boolean;
  picked: string | null;
  fresh: readonly string[];
  onPick: (mission: string) => void;
}) {
  const { room } = props;
  const reached = room.reached ?? [];
  const stars = useBestStars(props.connection, room, props.roomId);
  if (room.mission === undefined) return null;
  if (!props.isHost || reached.length < 2)
    return (
      <p class="mission">
        Mission: <strong>{missionName(room, room.mission)}</strong>{" "}
        <Stars n={stars[room.mission]} />
      </p>
    );
  const chosen =
    props.picked !== null && reached.includes(props.picked) ? props.picked : room.mission;
  // The same map as solo (P5-UX-01), for the group: what the room has reached is open.
  const missions = missionsOf(room).map((m) => {
    const open = reached.includes(m.id);
    const why = open
      ? ""
      : m.pack
        ? "With Obean Pass"
        : m.opensAfter
          ? "Opens after a win"
          : "Win the mission before";
    return { ...m, open, why, stars: stars[m.id] };
  });
  return (
    <fieldset class="card missions">
      <legend>Mission</legend>
      <MissionMap
        missions={missions}
        art={games.find((g) => g.id === room.game?.id)?.art}
        pressed={chosen}
        fresh={props.fresh}
        onPick={props.onPick}
      />
    </fieldset>
  );
}

/** Whether the next match is a battle that can be two against two, with four in the room. */
function teamBattle(room: RoomState, picked: string | null): boolean {
  const mission = picked ?? room.mission;
  return (
    missionsOf(room).find((m) => m.id === mission)?.teams === true && room.members.length === 4
  );
}

/**
 * Battle for four (ADR 0028): everyone for themselves, or two against two. The host picks, and
 * moves players across; everyone sees the sides.
 */
function TeamPicker(props: {
  connection: RoomConnection;
  room: RoomState;
  isHost: boolean;
  picked: string | null;
}) {
  const { room, connection } = props;
  if (!teamBattle(room, props.picked)) return null;
  const teams = room.teams;
  const online = connection.status.value.kind === "connected";
  return (
    <fieldset class="card teams-pick">
      <legend>Battle</legend>
      <div class="choices">
        {[false, true].map((on) => (
          <button
            type="button"
            key={String(on)}
            aria-pressed={(teams !== undefined) === on}
            disabled={!props.isHost || !online}
            onClick={() => {
              connection.send({ type: "room.teams", on });
            }}
          >
            {on ? "2 v 2" : "Everyone for themselves"}
          </button>
        ))}
      </div>
      {teams && (
        <div class="sides">
          {[0, 1].map((side) => (
            <div class="side" key={side}>
              <h3>Team {side + 1}</h3>
              <ul>
                {room.members
                  .filter((m) => teams[m.seat] === side)
                  .map((m) => (
                    <li key={m.seat}>
                      <span>{m.name}</span>
                      {props.isHost && (
                        <button
                          type="button"
                          class="link"
                          disabled={!online}
                          onClick={() => {
                            connection.send({ type: "room.team", seat: m.seat, team: 1 - side });
                          }}
                        >
                          Move to Team {2 - side}
                        </button>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </fieldset>
  );
}

/**
 * Missions only the Obean Pass unlocks (ADR 0022), once the room has won its first mission: the
 * first session is for playing together (G1). Shown only where the Pass is sold; with the switch
 * off, everyone unlocks them and there's nothing to show.
 */
function PassOffer(props: { room: RoomState; roomId: string }) {
  const playedOnce = (props.room.reached ?? []).length >= 2;
  const locked = playedOnce
    ? lockedPassMissions(missionsOf(props.room), props.room.reached ?? [])
    : [];
  const offered = useSignal(false);
  useEffect(() => {
    if (locked.length === 0) return;
    api.me().then(
      (me) => {
        offered.value = me.passOffered === true;
      },
      () => {
        // Can't tell: say nothing.
      },
    );
  }, [locked.length > 0]);
  if (locked.length === 0 || !offered.value) return null;
  return <PassMissions missions={locked} roomId={props.roomId} />;
}

/** Voice is offered unless the build turns it off (ADR 0008; production does until its notice). */
const VOICE_ON = import.meta.env.VITE_VOICE !== "off";

/**
 * Voice chat (P2-VOICE-02): opt in, see who's in voice, mute. Nothing is recorded, and when voice
 * can't work here the card says why while the game and chat carry on.
 */
function VoiceCard({
  voice,
  room,
  mySeat,
}: {
  voice: VoiceSession;
  room: RoomState;
  mySeat: string | null;
}) {
  const status = voice.status.value;
  const others = room.members.filter((m) => m.seat !== mySeat);
  const othersInVoice = others.filter((m) => m.voice !== undefined);
  const join = () => {
    if (mySeat) void voice.join(mySeat);
  };
  return (
    <div class="card voice" role="group" aria-label="Voice chat">
      <h2>Voice chat</h2>
      {status === "off" && (
        <>
          <p class="hint">
            Talk while you play. Your browser will ask to use the microphone. Nothing is recorded.
          </p>
          <button type="button" onClick={join}>
            Join voice
          </button>
        </>
      )}
      {status === "asking" && <p role="status">Allow the microphone in your browser's prompt…</p>}
      {(status === "connecting" || status === "on") && (
        <>
          <p role="status">
            {status === "on"
              ? `In voice with ${othersInVoice.map((m) => m.name).join(" and ")}.`
              : othersInVoice.length > 0
                ? "Connecting…"
                : others.length === 0
                  ? "You're in voice. Invite a friend to talk: they join with your invite link."
                  : "You're in voice. Waiting for your teammate to join voice."}
          </p>
          <div class="row">
            <button
              type="button"
              aria-pressed={voice.muted.value}
              onClick={() => {
                voice.setMuted(!voice.muted.value);
              }}
            >
              {voice.muted.value ? "Unmute mic" : "Mute mic"}
            </button>
            <button
              type="button"
              class="secondary"
              onClick={() => {
                voice.leave();
              }}
            >
              Leave voice
            </button>
          </div>
        </>
      )}
      {status === "unavailable" && (
        <>
          <p role="alert">{voice.problem.value}</p>
          <button type="button" class="secondary" onClick={join}>
            Try again
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Voice in the match's own bar (P2-VOICE-02): the mic switch once in voice, and a way in for
 * someone who skipped voice in the lobby (the device lab's first mixed-pair run, 2026-10-11: until
 * then a match had to end before they could join).
 */
function MatchVoice({ voice, mySeat }: { voice: VoiceSession; mySeat: string | null }) {
  const status = voice.status.value;
  if (status === "on" || status === "connecting")
    return (
      <button
        type="button"
        class="mic icon"
        aria-pressed={!voice.muted.value}
        aria-label={voice.muted.value ? "Mic off" : "Mic on"}
        title={voice.muted.value ? "Mic off" : "Mic on"}
        onClick={() => {
          voice.setMuted(!voice.muted.value);
        }}
      >
        <MicIcon on={!voice.muted.value} />
      </button>
    );
  const label = status === "asking" ? "Joining…" : "Join voice";
  return (
    <button
      type="button"
      class="mic icon"
      aria-label={label}
      title={label}
      disabled={status === "asking" || mySeat === null}
      onClick={() => {
        if (mySeat) void voice.join(mySeat);
      }}
    >
      <VoiceIcon />
    </button>
  );
}

/** What joining voice from the match needs said: the browser's prompt, or why it didn't work. */
function MatchVoiceNote({ voice }: { voice: VoiceSession }) {
  const status = voice.status.value;
  if (status === "asking")
    return (
      <p class="voice-note" role="status">
        Allow the microphone in your browser's prompt. Nothing is recorded.
      </p>
    );
  if (status === "unavailable" && voice.problem.value)
    return (
      <p class="voice-note" role="alert">
        {voice.problem.value}
      </p>
    );
  return null;
}

/**
 * Your role for the next match (P2-WEB-03): the room's game's loadouts. Not picking is fine: the
 * game then gives you one that complements your teammate. Tap your pick again to undo it.
 */
const seatColourVar = (colour: number) => SEAT_COLOURS[colour % SEAT_COLOURS.length] ?? "";

/**
 * Your bean's extra (ADR 0018): the room shows it at once (`room.bean`), and the api keeps it for
 * your next rooms, on every browser you're signed in on.
 */
function BeanPicker({ connection, room }: { connection: RoomConnection; room: RoomState }) {
  const failed = useSignal(false);
  const me = room.members.findIndex((m) => m.seat === connection.seat.value);
  const member = room.members[me];
  if (!member) return null;
  const colour = member.colour ?? me;
  const mine = member.bean ?? null;
  const online = connection.status.value.kind === "connected";
  const pick = (bean: string | null) => {
    connection.send({ type: "room.bean", bean });
    failed.value = false;
    api.setBean(bean).catch(() => {
      failed.value = true;
    });
  };
  const label = EXTRAS.find((e) => e.id === mine)?.label ?? "Plain";
  // Looks unlocked by level (ADR 0043, #585): the room's word for this bean's level.
  const level = member.level ?? 1;
  return (
    <details class="card beans">
      <summary>
        <Bean colour={colour} extra={mine} size={32} />
        <span>
          Your bean: <b>{label}</b>
          {member.level !== undefined && <> · Level {member.level}</>}
        </span>
        <span class="change">Change</span>
      </summary>
      <fieldset class="roles">
        <legend class="visually-hidden">Your bean</legend>
        <div class="choices">
          {[{ id: null, label: "Plain" }, ...EXTRAS].map((e) => {
            const opensAt = extraLevel(e.id);
            const locked = opensAt > level && mine !== e.id;
            return (
              <button
                type="button"
                key={e.id ?? "plain"}
                aria-pressed={mine === e.id}
                disabled={!online || locked}
                onClick={() => {
                  pick(e.id);
                }}
              >
                <Bean colour={colour} extra={e.id} size={44} />
                <span>{e.label}</span>
                {locked && <span class="opens">Level {opensAt}</span>}
              </button>
            );
          })}
        </div>
        <p class="hint" role={failed.value ? "alert" : undefined}>
          {failed.value
            ? "Your friends see it now, but it couldn't be saved for next time."
            : "Your seat gives the colour. The extra is yours, in every room. Beans from every game raise your level and open more."}
        </p>
      </fieldset>
    </details>
  );
}

function RolePicker({ connection, room }: { connection: RoomConnection; room: RoomState }) {
  const choices = loadoutsOf(room);
  if (choices.length === 0) return null;
  const mine = room.members.find((m) => m.seat === connection.seat.value)?.loadout ?? null;
  const online = connection.status.value.kind === "connected";
  return (
    <fieldset class="card roles">
      <legend>Your role</legend>
      <div class="choices">
        {choices.map((c) => (
          <button
            type="button"
            key={c.id}
            aria-pressed={mine === c.id}
            disabled={!online}
            onClick={() => {
              connection.send({ type: "room.loadout", loadout: mine === c.id ? null : c.id });
            }}
          >
            <strong>{c.name}</strong>
            <span>{c.summary}</span>
          </button>
        ))}
      </div>
      <p class="hint">
        {mine === null
          ? "Or let the game pick one that complements your teammate."
          : "Tap it again to let the game pick."}
      </p>
    </fieldset>
  );
}

/** The room's game while it's loaded, kept across rematches. */
interface Loaded {
  ref: GameRef;
  game: AnyGame;
  host: GameHost;
}

/**
 * A match in the room (P2-RT-05). When the room says what to load (`match.load`), the game loads
 * and the room is told it's ready; from the first snapshot frame it plays, drawing what the room
 * sends and sending this player's input. It covers the page while the match is on, and shows the
 * result after with the host's way to play again.
 */
function MatchArea(props: {
  connection: RoomConnection;
  room: RoomState;
  isHost: boolean;
  /** Covering the page (a match on, or its result not yet left for the room). */
  shown: boolean;
  /** The room's voice session, for the in-match mic toggle (P2-VOICE-02). */
  voice: VoiceSession | null;
  onLeave: () => void;
  onBackToRoom: () => void;
}) {
  const { connection, room, isHost } = props;
  const stage = useRef<HTMLDivElement>(null);
  /** Which of the game's tips comes next, after a loss (ADR 0043). */
  const tipTurn = useRef(0);
  // The first play teaches (P5-UX-05), as in solo.
  const teach = useTeach(
    room.game?.id ?? "",
    games.find((g) => g.id === room.game?.id)?.teach ?? [],
  );
  const teachNow = useRef(teach);
  teachNow.current = teach;
  const input = useMemo(() => new PlayerInput(), []);
  const touchUi = useMemo(() => matchMedia("(pointer: coarse)").matches, []);
  const loaded = useRef<Loaded | null>(null);
  const driver = useRef<OnlineDriver<unknown, unknown> | null>(null);
  const status = useSignal("");
  const loadFailed = useSignal(false);
  const load = connection.match.value;
  const ended = connection.ended.value;
  const inMatch = props.shown;
  // Today's daily played as a room counts towards your best of the day too (P4-TANK-03); signed
  // in, a new best goes to friends as well (privacy policy 1.8).
  useEffect(() => {
    if (!ended || load?.daily === undefined) return;
    const { best, isNew } = recordDaily(
      load.game.id,
      load.daily,
      { result: ended.result, stats: ended.stats },
      localStorage,
      loaded.current?.game.manifest.dailyScore,
    );
    if (isNew) void shareDaily(load.game.id, load.daily, best, api);
  }, [ended]);
  // Beans (ADR 0043): each player's own, more with friends; in Battle only the winners' win.
  const beans = useSignal<{ earned: Earned; total: number } | null>(null);
  // Achievements earned by this play (ADR 0043, P5-LOOP-03).
  const feats = useSignal<Feat[]>([]);
  useEffect(() => {
    beans.value = null;
    feats.value = [];
    if (!ended || !load) return;
    const gameId = load.game.id;
    const me = load.players.find((p) => p.slot === load.slot);
    const rules = games.find((g) => g.id === gameId)?.beans ?? [];
    let current = true;
    void profileId().then((profile) => {
      if (!current) return;
      const { wallet, earned } = award(
        loadWallet(profile),
        {
          gameId,
          decided: true,
          won: wonBy(ended, load.slot, me?.team),
          stats: ended.stats,
          daily: load.daily !== undefined,
          together: room.members.length >= 2,
        },
        rules,
        dayOf(Date.now()),
      );
      beans.value = { earned, total: beansOf(wallet) };
      void keepFeats(
        {
          gameId,
          missionId: load.missionId,
          won: wonBy(ended, load.slot, me?.team),
          stats: ended.stats,
          together: room.members.length >= 2,
        },
        games.find((g) => g.id === gameId)?.achievements ?? [],
        {
          level: levelOf(beansOf(wallet)).level,
          games: games.filter((g) => g.stage === "live").map((g) => g.id),
        },
        profile,
        dayOf(Date.now()),
      ).then((got) => {
        if (current) feats.value = got;
      });
      // Signed in, the person's other devices' beans come back with the sync (#585).
      void keepWallet(wallet, profile).then((kept) => {
        if (current) beans.value = { earned, total: beansOf(kept) };
        // The room shows the level on this bean (ADR 0043, #585).
        connection.send({ type: "room.level", level: levelOf(beansOf(kept)).level });
      });
    });
    return () => {
      current = false;
    };
  }, [ended]);
  // Ads without the Pass, unless the host has it (ADR 0026): a banner at the top, over the bar.
  const ads = adsForMe.value === true && room.adFree !== true;
  const banner = useBannerInPlay(ads);
  // Paused while a player is away (P2-RT-06, P2-WEB-06): this one reconnecting, another dropped,
  // or anyone's page in the background for 3 s (the room marks them `away`).
  const reconnecting = connection.status.value.kind === "reconnecting";
  // A slot a bot took over (ADR 0004) waits for nobody: its player left for good.
  const botSlots = new Set(room.phase === "playing" ? (room.bots ?? []).map((b) => b.slot) : []);
  const playerSeats = new Set(
    load?.players.filter((p) => !botSlots.has(p.slot)).map((p) => p.seat) ?? [],
  );
  const mySeat = connection.seat.value;
  const away = room.members.filter(
    (m) => playerSeats.has(m.seat) && m.seat !== mySeat && (!m.connected || m.away === true),
  );
  const meAway = room.members.some((m) => m.seat === mySeat && m.away === true);
  const paused = room.phase === "playing" && (reconnecting || meAway || away.length > 0);
  const pausedNow = useRef(paused);
  pausedNow.current = paused;

  useEffect(
    () => () => {
      connection.onFrame = null;
      loaded.current?.host.exit();
      input.dispose();
    },
    [],
  );

  // Told what to load: the game (unless it's here already), then ready; it starts on the first frame.
  useEffect(() => {
    if (!load) return;
    let cancelled = false;
    const isCancelled = () => cancelled;
    driver.current = null;
    connection.onFrame = null;
    const ref: GameRef = { id: load.game.id, version: load.game.version };
    rememberLastPlay(load.game.id); // the start page's Carry on (P5-UX-04)
    const prepare = async (): Promise<Loaded | null> => {
      const here = loaded.current;
      if (here && here.ref.id === ref.id && here.ref.version === ref.version) return here;
      here?.host.exit();
      loaded.current = null;
      const loadStartedAt = performance.now();
      const game = await loadGame(ref);
      const container = stage.current;
      if (isCancelled() || !container) return null;
      const host = await GameHost.load({ container, game, ref, track, loadStartedAt });
      if (isCancelled()) {
        host.exit();
        return null;
      }
      loaded.current = { ref, game, host };
      return loaded.current;
    };
    status.value = "Loading the game…";
    loadFailed.value = false;
    prepare()
      .then((ready) => {
        if (!ready) return;
        const { game, host } = ready;
        const players = load.players.map((p) => ({ slot: p.slot, personId: p.seat }));
        const onFrame = (frame: SnapshotFrame) => {
          if (driver.current) {
            driver.current.receive(frame);
            return;
          }
          // Inputs go only on the socket this match was loaded on (see RoomConnection.socket).
          const socket = connection.socket;
          const playFrom = performance.now();
          teachNow.current.restart(); // each match times its own first move
          driver.current = new OnlineDriver<unknown, unknown>({
            rules: game.rules,
            prediction: host.prediction,
            slot: load.slot,
            players: players.length,
            first: frame,
            input: () => {
              const controls = input.sample();
              const mine = host.input(controls);
              const at = teachNow.current.see(controls, mine, performance.now());
              // How long a player takes to do something (P5-UX-05), once a match.
              if (at !== null)
                track("first_move", {
                  game_id: load.game.id,
                  game_version: load.game.version,
                  mission_id: load.missionId,
                  place: "room",
                  ms: Math.round(at - playFrom),
                  first_play: teachNow.current.teaching,
                });
              return mine;
            },
            send: (seq, value) => {
              if (connection.socket === socket)
                connection.send({ type: "match.in", b: [[seq, value]] });
            },
            now: () => performance.now(),
          });
          if (pausedNow.current) driver.current.pause(); // back in a match that waits for someone
          const ctx = { missionId: load.missionId, mySlot: load.slot, players };
          const ids = { matchId: load.matchId ?? null, roomId: connection.roomId };
          // Traced (ADR 0035): the room chose this match; this device records its own view.
          const trace = load.trace
            ? {
                id: `${load.matchId ?? connection.roomId}-s${String(load.slot)}`,
                why: load.trace,
                place: "room" as const,
                slot: load.slot,
              }
            : undefined;
          host.start({ ...ctx, ...ids, ...(trace ? { trace } : {}) }, driver.current);
          status.value = "";
        };
        connection.onFrame = onFrame;
        // A frame that came while the game was loading (rejoining a match): start from it.
        if (connection.latestFrame) onFrame(connection.latestFrame);
        else status.value = "Waiting for everyone to load…";
        connection.send({ type: "match.ready" });
      })
      .catch((err: unknown) => {
        if (err instanceof GameUnavailable && reloadOnce(err.ref)) return;
        status.value = err instanceof Error ? err.message : String(err);
        loadFailed.value = true;
        diagMark("load_failed");
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Nobody steps or sends input while a player is away; play resumes from fresh snapshots.
  useEffect(() => {
    if (paused) driver.current?.pause();
    else driver.current?.resume();
  }, [paused]);

  // The page going to the background, and coming back, is the room's to know (P2-WEB-06): after
  // 3 s in the background the match waits for this player, as for a dropped connection.
  const playing = inMatch && room.phase === "playing";
  const connected = connection.status.value.kind === "connected";
  useEffect(() => {
    if (!playing || !connected) return;
    const report = () => {
      connection.send({ type: "match.away", away: document.visibilityState === "hidden" });
    };
    // The match started in the background, or the page came back on a new connection while in it.
    if (document.visibilityState === "hidden") report();
    document.addEventListener("visibilitychange", report);
    // A report sent as the connection dropped is lost: while hidden, say it again now and then (the
    // room counts the time from the first).
    const again = setInterval(() => {
      if (document.visibilityState === "hidden") report();
    }, AWAY_REPORT_MS);
    return () => {
      document.removeEventListener("visibilitychange", report);
      clearInterval(again);
    };
  }, [playing, connected]);

  // Decided: the game shows the result.
  useEffect(() => {
    if (ended) driver.current?.end({ result: ended.result, stats: ended.stats });
  }, [ended]);

  // The result as a card to share (P2-WEB-07), made as soon as it's in.
  const card = useSignal<ResultCard | null>(null);
  useEffect(() => {
    card.value = null;
    const game = loaded.current?.game;
    if (!ended || !load || !game) return;
    const { manifest } = game;
    const result = {
      result: ended.result,
      stats: ended.stats,
      mission: manifest.missions.find((m) => m.id === load.missionId)?.name ?? manifest.name,
      game: manifest.name,
      tickHz: manifest.tickHz,
      players: room.members.map((m, i) => ({ colour: m.colour ?? i, extra: m.bean ?? null })),
      daily: load.daily !== undefined,
      ...(load.daily ? { dailyNumber: dailyNumber(load.daily) } : {}),
    };
    const props = {
      match_id: load.matchId ?? null,
      room_id: connection.roomId,
      result: ended.result,
      mission_id: load.missionId,
    };
    let current = true;
    void cardFile(result).then((file) => {
      // Today's daily shares its numbered line (ADR 0041); any other match, its sentence.
      const text =
        result.dailyNumber !== undefined
          ? dailyShareLine(
              {
                ...result,
                gameId: manifest.id,
                dailyNumber: result.dailyNumber,
                score: manifest.dailyScore,
              },
              location.origin,
            )
          : shareText(result, location.origin);
      if (current && file) card.value = { file, text, props };
    });
    return () => {
      current = false;
    };
  }, [ended]);

  // Called off (a player left, or loading ran out): the match stops, the game stays loaded.
  useEffect(() => {
    if (room.phase !== "lobby") return;
    connection.onFrame = null;
    driver.current = null;
    status.value = "";
    loaded.current?.host.stop();
  }, [room.phase]);

  const hostName = room.members.find((m) => m.seat === room.host)?.name ?? "the host";
  // After a win the host can go on to the next mission (P2-TANK-07), or play this one again.
  const next = nextMission(room);
  /** The host's next step, counted like solo's (ADR 0043). */
  const stepTaken = (step: "next" | "again", main: boolean) => {
    track("next_step", {
      step,
      main,
      game_id: room.game?.id ?? "",
      mission_id: room.mission ?? "",
      place: "room",
    });
  };
  const playAgain = () => {
    stepTaken("again", !(next !== null && ended?.result === "win"));
    connection.send({
      type: "room.start",
      ...(room.mission === undefined ? {} : { mission: room.mission }),
    });
  };
  // After a loss, a tip for everyone (ADR 0043), a different one each match.
  const tips = games.find((g) => g.id === room.game?.id)?.tips ?? [];
  const tip = useMemo(
    () => (ended?.result === "loss" ? tipFor(tips, tipTurn.current++) : null),
    // Once per result: a new tip for each match that's lost.
    [ended],
  );
  // What a win opens (P5-LOOP-01): after a loss, what winning would open; after a win, what it did.
  // The missions reached before the result tell a first win from a win again.
  const reachedBefore = useRef<readonly string[]>([]);
  useEffect(() => {
    if (room.phase !== "results") reachedBefore.current = room.reached ?? [];
  }, [room]);
  const unlock =
    ended && room.phase === "results" && room.mission !== undefined
      ? unlockLine(
          missionsOf(room),
          room.mission,
          ended.result,
          (id) => (room.reached ?? []).includes(id),
          (id) => reachedBefore.current.includes(id),
        )
      : null;
  return (
    <section
      class={`play online${barred(touchUi, room) ? ` ${touchOf(room)}` : ""}${banner ? " ad" : ""}${inMatch ? "" : " hidden"}${inMatch && !status.value && room.phase === "playing" && teachingNow(teach) ? " teaching" : ""}`}
      aria-hidden={!inMatch}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
    >
      {ads && inMatch && <AdBanner where="room" playing gameId={room.game?.id} />}
      {/* The shell's own bar, so its controls never cover the game's HUD: what's going on to the
          left (shortened to fit), then sound and voice as icons, and Leave in words. */}
      <header class="match-top">
        <div class="badges">
          {load?.daily !== undefined && (
            <span class="badge daily" role="status">
              📅 Today's daily
            </span>
          )}
          {/* Someone left and a bot plays their slot (ADR 0004): say so, so their moves aren't a mystery. */}
          {room.phase === "playing" &&
            room.bots?.map((b) => (
              <span key={b.slot} class="badge bot" role="status">
                🤖 Bot playing for {b.for}
              </span>
            ))}
        </div>
        <button
          type="button"
          class="sound icon"
          aria-pressed={soundOn.value}
          aria-label={soundOn.value ? "Sound on" : "Sound off"}
          title={soundOn.value ? "Sound on" : "Sound off"}
          onClick={() => {
            setSoundOn(!soundOn.value);
          }}
        >
          <SoundIcon on={soundOn.value} />
        </button>
        <button
          type="button"
          class="settings icon"
          aria-label="Settings"
          title="Settings"
          onClick={openSettings}
        >
          <SettingsIcon />
        </button>
        {props.voice && <MatchVoice voice={props.voice} mySeat={connection.seat.value} />}
        <button type="button" class="leave" onClick={props.onLeave}>
          Leave
        </button>
      </header>
      <div ref={stage} class="stage" />
      {inMatch &&
        room.phase === "playing" &&
        (touchUi ? (
          <>
            <TouchControls touch={input.touch} buttons={buttonsOf(room)} mode={touchOf(room)} />
            {/* Shown only while the phone is upright (styles.css): the map gets bigger sideways.
                Swipe games are played upright (ADR 0023), so they don't ask. */}
            {touchOf(room) === "stick" && (
              <p class="rotate-hint">Turn sideways for a bigger view.</p>
            )}
          </>
        ) : touchOf(room) === "tap" && buttonsOf(room).length === 0 ? null : (
          // A tap game without buttons shows its own keys (ADR 0027): "Move" would be wrong.
          <p class="hint">{keyboardHint(buttonsOf(room))}</p>
        ))}
      {inMatch && status.value && <p role="status">{status.value}</p>}
      {inMatch && !status.value && room.phase === "playing" && teach.teaching && (
        <TeachCard teach={teach} touch={touchUi} />
      )}
      {inMatch && loadFailed.value && <TellUs />}
      {inMatch && props.voice && <MatchVoiceNote voice={props.voice} />}
      {inMatch && paused && (
        <div class="paused" role="status">
          <strong>Paused</strong>
          <span>
            {reconnecting
              ? "Reconnecting…"
              : away.length > 0
                ? `Waiting for ${away.map((m) => m.name).join(" and ")} to come back…`
                : "Picking up where you left off…"}
          </span>
        </div>
      )}
      {inMatch && room.phase === "results" && (
        <div class="match-bar">
          {/* The sheet covers the game's own result on a phone, so it says it too (ADR 0043). */}
          {ended && load && (
            <h2 class="result-line">
              {headlineOf(
                ended,
                {
                  slot: load.slot,
                  team: load.players.find((p) => p.slot === load.slot)?.team,
                },
                (slot) => {
                  const seat = load.players.find((p) => p.slot === slot)?.seat;
                  return room.members.find((m) => m.seat === seat)?.name ?? "A bot";
                },
              )}
            </h2>
          )}
          {ended?.result === "win" && <StarsMoment n={ended.stats["stars"]} />}
          {beans.value && <BeansEarned earned={beans.value.earned} total={beans.value.total} />}
          <FeatsEarned earned={feats.value} />
          <HourNote />
          {tip && (
            <p class="tip">
              <strong>Tip:</strong> {tip}
            </p>
          )}
          {unlock && <p class="unlock">{unlock}</p>}
          {isHost ? (
            next !== null && ended?.result === "win" ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    stepTaken("next", true);
                    connection.send({ type: "room.start", mission: next });
                  }}
                >
                  Next mission
                </button>
                <button type="button" class="secondary" onClick={playAgain}>
                  Play again
                </button>
              </>
            ) : (
              <button type="button" onClick={playAgain}>
                {ended?.result === "loss" ? "Try again" : "Play again"}
              </button>
            )
          ) : (
            <p>Waiting for {hostName} to play on</p>
          )}
          {card.value && <ShareResult card={card.value} />}
          <AddFriends roomId={connection.roomId} />
          {room.game && <FeedbackPrompt gameId={room.game.id} />}
          <button type="button" class="secondary" onClick={props.onBackToRoom}>
            Back to the room
          </button>
        </div>
      )}
    </section>
  );
}

/** The room once you're in: who's here, a link for a friend (host), and the way out. */
function RoomView({ roomId, connection }: { roomId: string; connection: RoomConnection }) {
  const invite = useSignal<Invite | null>(recallInvite(roomId));

  // A host who lost the link (a reload in a fresh tab) gets a new one.
  const mySeat = connection.seat.value;
  const isHost = mySeat !== null && connection.room.value?.host === mySeat;
  useEffect(() => {
    if (!isHost || invite.value) return;
    api
      .newInvite(roomId)
      .then(({ invite: fresh }) => {
        rememberInvite(roomId, fresh);
        invite.value = fresh;
      })
      .catch(() => undefined);
  }, [isHost]);

  const status = connection.status.value;
  const room = connection.room.value;

  // This bean's level (ADR 0043, #585), for the room to show: from the beans kept here, synced
  // for a signed-in person, each time this page connects.
  useEffect(() => {
    if (status.kind !== "connected") return;
    let current = true;
    void profileId().then(async (profile) => {
      const wallet = await keepWallet(loadWallet(profile), profile);
      if (current) connection.send({ type: "room.level", level: levelOf(beansOf(wallet)).level });
    });
    return () => {
      current = false;
    };
  }, [status.kind]);

  // Came from a game's "Play with friends" (/new?game=<id>): the new room starts with that game.
  const roomGameId = room?.game?.id;
  // Friends see which game this room is playing (ADR 0031).
  usePlaying(roomGameId);
  const inLobby = room?.phase === "lobby";
  useEffect(() => {
    if (!isHost || !inLobby || roomGameId === undefined) return;
    const wanted = takeRoomGame(roomId);
    if (wanted && wanted !== roomGameId && games.some((g) => g.id === wanted))
      connection.send({ type: "room.select", game: wanted });
  }, [isHost, inLobby, roomGameId === undefined]);

  // Voice (P2-VOICE-02): its own session beside the room's, signalled through it.
  const voice = useMemo(
    () =>
      VOICE_ON
        ? new VoiceSession({
            send: (msg) => {
              connection.send(msg);
            },
            credentials: () => api.voiceCredentials(roomId),
            track,
          })
        : null,
    [connection],
  );
  useEffect(() => {
    if (!voice) return;
    connection.onVoiceSignal = (from, data) => {
      void voice.signal(from, data);
    };
    return () => {
      connection.onVoiceSignal = null;
      voice.leave();
    };
  }, [voice]);
  useEffect(() => {
    if (voice && room) voice.sync(room.members);
  }, [voice, room]);
  useEffect(() => {
    if (!voice) return;
    for (const m of room?.members ?? []) voice.mutePeer(m.seat, connection.muted.value.has(m.seat));
  }, [voice, room, connection.muted.value]);
  useEffect(() => {
    if (voice && status.kind === "connected") voice.reconnected();
  }, [voice, status.kind]);
  const leaveRoom = () => {
    rememberLeft(roomId);
    connection.leave();
    navigate("/new");
  };
  // Leaving with friends here (#616): the choice to take them along to a new room, so nobody is
  // left waiting in this one.
  const leaving = useSignal<"ask" | "moving" | null>(null);
  const moveFull = useSignal<Hosting | null>(null);
  const moveError = useSignal<string | null>(null);
  const othersHere = room?.members.some((m) => m.connected && m.seat !== mySeat) ?? false;
  const askLeave = () => {
    if (othersHere) leaving.value = "ask";
    else leaveRoom();
  };
  const bringEveryone = async () => {
    leaving.value = "moving";
    moveError.value = null;
    try {
      // A failed check isn't a refusal, as on the start page.
      const hosting = await api.me().then(
        (me) => me.hosting,
        () => null,
      );
      if (hosting && hosting.nextFreeAt !== null) {
        moveFull.value = hosting;
        leaving.value = "ask";
        return;
      }
      const { roomId: next, invite } = await api.createRoom();
      const secret = secretFromHash(new URL(invite.url, location.origin).hash);
      rememberInvite(next, invite);
      if (room?.game) rememberRoomGame(next, room.game.id);
      connection.leave(secret ? { room: next, secret } : undefined);
      navigate(`/r/${next}`);
    } catch (err) {
      moveError.value =
        err instanceof ApiError && err.code === "on_hold"
          ? ON_HOLD
          : "Couldn't make the new room. Try again in a moment.";
      leaving.value = "ask";
    }
  };
  const follow = (to: RoomMoved["to"]) => {
    rememberFollow(to.room);
    connection.leave();
    navigate(`/r/${to.room}#${to.secret}`);
  };
  const connectedCount = room?.members.filter((m) => m.connected).length ?? 0;
  const blocker = room
    ? startBlocker(
        connectedCount,
        playersOf(room),
        games.find((g) => g.id === room.game?.id)?.displayName ?? "This game",
      )
    : "Waiting for a friend to join";
  const together = blocker === null;
  // A match covers the page (the room under it is out of reach, and of screen readers), until its
  // result is left for the room.
  const load = connection.match.value;
  const backInRoom = useSignal<MatchLoad | null>(null);
  // Today's daily (P4-TANK-03), for the host's button: the same mission and seed for everyone.
  const gameInfo = games.find((g) => g.id === room?.game?.id);
  const today = gameInfo ? dailyOf(gameInfo, dayOf(Date.now())) : null;
  /** The host's pick of mission for the next match, if any (P2-TANK-07). */
  const picked = useSignal<string | null>(null);
  // The missions open when the last match started, so the lobby's map can open what it reached
  // with its moment (P5-UX-03).
  const reachedAtPlay = useRef<readonly string[] | null>(null);
  const fresh = useSignal<readonly string[]>([]);
  const phaseNow = room?.phase;
  useEffect(() => {
    if (phaseNow !== "playing" || reachedAtPlay.current !== null) return;
    reachedAtPlay.current = room?.reached ?? [];
    fresh.value = [];
  }, [phaseNow]);
  // Two against two needs two a side (ADR 0028).
  const sides =
    blocker === null && room && room.teams && teamBattle(room, picked.value)
      ? teamsBlocker(room.teams)
      : null;
  const inMatch =
    load !== null &&
    room !== null &&
    room.phase !== "lobby" &&
    !(room.phase === "results" && backInRoom.value === load);
  const roleName = (id: string) =>
    (room ? loadoutsOf(room) : []).find((l) => l.id === id)?.name ?? id;
  const hostName = room?.members.find((m) => m.seat === room.host)?.name ?? "the host";
  // Ads without the Pass (ADR 0026): none while the host has it (room.adFree), for anyone.
  useEffect(loadAds, []);
  const ads = adsForMe.value === true && room?.adFree !== true;
  return (
    <section class="room">
      {ads && room && !inMatch && <AdBanner where="room" gameId={room.game?.id} />}
      <h1>{room ? `${hostName}'s room` : "Room"}</h1>
      {status.kind === "connecting" && <p role="status">Joining…</p>}
      {status.kind === "closed" && status.reason === "hosting_limit" && (
        <HostingLimit
          hosting={hostingOf(status.details) ?? { limit: 0, used: 0, nextFreeAt: null }}
          roomId={roomId}
          back={`/r/${roomId}`}
        />
      )}
      {status.kind === "closed" && status.reason !== "hosting_limit" && (
        <div class="card">
          <p role="alert">{closedMessage(status)}</p>
          {/* The connection was lost, not refused: back in, as when opening the link (#644). */}
          {canRejoin(status) && (
            <button
              type="button"
              onClick={() => {
                connection.rejoin();
              }}
            >
              Rejoin the room
            </button>
          )}
          <button
            type="button"
            class={canRejoin(status) ? "secondary" : undefined}
            onClick={() => {
              navigate("/new");
            }}
          >
            Back to the start
          </button>
          <TellUs />
        </div>
      )}
      {(status.kind === "connected" || status.kind === "reconnecting") && room && (
        <>
          <div class="lobby" hidden={inMatch}>
            {status.kind === "reconnecting" && <p role="status">Reconnecting…</p>}
            {connection.moved.value && (
              <div class="card moved" role="group" aria-label="A new room">
                <p role="status">
                  {connection.moved.value.name} made a new room and asked everyone along.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (connection.moved.value) follow(connection.moved.value.to);
                  }}
                >
                  Join {connection.moved.value.name}'s new room
                </button>
                <button
                  type="button"
                  class="secondary"
                  onClick={() => {
                    connection.moved.value = null;
                  }}
                >
                  Stay here
                </button>
              </div>
            )}
            {connection.departed.value && (
              <div class="card notice">
                <p role="status">{connection.departed.value}</p>
                <button
                  type="button"
                  class="secondary"
                  onClick={() => {
                    connection.departed.value = null;
                  }}
                >
                  OK
                </button>
              </div>
            )}
            <ul class="roster" aria-label="Players in the room">
              {room.members.map((m, i) => (
                <li
                  key={m.seat}
                  class={`${m.connected && m.away !== true ? "here" : "away"}${voice?.speaking.value.has(m.seat) ? " speaking" : ""}`}
                  style={{ "--seat": seatColourVar(m.colour ?? i) }}
                >
                  <Bean
                    colour={m.colour ?? i}
                    extra={m.bean ?? null}
                    mood={moodOf({
                      connected: m.connected,
                      ...(m.away === undefined ? {} : { away: m.away }),
                      speaking: voice?.speaking.value.has(m.seat) ?? false,
                      won: room.phase === "results" && connection.ended.value?.result === "win",
                    })}
                    badge={(m.colour ?? i) + 1}
                    size={48}
                  />
                  <span class="name">{m.name}</span>
                  {m.level !== undefined && <span class="badge level">Level {m.level}</span>}
                  {m.seat === room.host && <span class="badge">host</span>}
                  {m.seat === connection.seat.value && <span class="badge">you</span>}
                  {m.loadout !== undefined && <span class="role">{roleName(m.loadout)}</span>}
                  {(!m.connected || m.away === true) && <span class="badge">away</span>}
                  {connection.muted.value.has(m.seat) && <span class="badge">muted</span>}
                  {m.voice !== undefined && (
                    <span
                      class={`badge voice${voice?.speaking.value.has(m.seat) ? " speaking" : ""}`}
                    >
                      {m.voice === "muted"
                        ? "mic off"
                        : voice?.speaking.value.has(m.seat)
                          ? "talking"
                          : "in voice"}
                    </span>
                  )}
                  {m.seat !== connection.seat.value && (
                    <MemberActions connection={connection} member={m} isHost={isHost} />
                  )}
                </li>
              ))}
            </ul>
            {/* Arrived during a match (the rest are playing it): say what happens, rather than a
                lobby that seems to wait for nothing. A new match takes everyone in the room. */}
            {(room.phase === "loading" || room.phase === "playing") && (
              <p class="hint" role="status">
                A match is on. You'll play in the next one, when{" "}
                {isHost ? "you start it" : `${hostName} starts it`}.
              </p>
            )}
            <BeanPicker connection={connection} room={room} />
            {isHost && invite.value && room.members.length < playersOf(room).max && (
              <InviteCard invite={invite.value} />
            )}
            {room.members.length < playersOf(room).max && <InviteFriends roomId={roomId} />}
            {voice && <VoiceCard voice={voice} room={room} mySeat={connection.seat.value} />}
            {(room.phase === "lobby" || room.phase === "results") && (
              <GamePicker connection={connection} room={room} isHost={isHost} />
            )}
            {(room.phase === "lobby" || room.phase === "results") && (
              <MissionPicker
                connection={connection}
                roomId={roomId}
                room={room}
                isHost={isHost}
                picked={picked.value}
                fresh={fresh.value}
                onPick={(mission) => {
                  picked.value = mission;
                }}
              />
            )}
            {(room.phase === "lobby" || room.phase === "results") && (
              <TeamPicker
                connection={connection}
                room={room}
                isHost={isHost}
                picked={picked.value}
              />
            )}
            {(room.phase === "lobby" || room.phase === "results") && (
              <PassOffer room={room} roomId={roomId} />
            )}
            {(room.phase === "lobby" || room.phase === "results") && (
              <RolePicker connection={connection} room={room} />
            )}
            {(room.phase === "lobby" || room.phase === "results") &&
              (isHost ? (
                <>
                  <button
                    type="button"
                    disabled={!together || sides !== null}
                    onClick={() => {
                      const mission = picked.value;
                      const known = mission !== null && (room.reached ?? []).includes(mission);
                      connection.send({ type: "room.start", ...(known ? { mission } : {}) });
                    }}
                  >
                    {blocker !== null
                      ? blocker
                      : sides !== null
                        ? sides
                        : room.phase === "results"
                          ? "Play again"
                          : "Start the mission"}
                  </button>
                  {today && (
                    <button
                      type="button"
                      class="secondary"
                      disabled={!together}
                      onClick={() => {
                        connection.send({ type: "room.start", daily: true });
                      }}
                    >
                      Play today's daily #{dailyNumber(dayOf(Date.now()))}:{" "}
                      {gameInfo?.missions.find((m) => m.id === today.missionId)?.name ??
                        today.missionId}
                    </button>
                  )}
                </>
              ) : (
                <p class="hint">
                  Waiting for {hostName} to {room.phase === "results" ? "play again" : "start"}
                </p>
              ))}
            {(room.phase === "lobby" || room.phase === "results") &&
              connectedCount < playersOf(room).min &&
              room.game && (
                // Alone in the room (ADR 0020): solo with bots in another tab, while this one keeps
                // the room open for the friend.
                <div class="card solo-friend">
                  <p>
                    <strong>Nobody here yet?</strong> Play solo with bot teammates while you wait.
                    This room stays open in this tab, so your friend can still join.
                  </p>
                  <a
                    class="button"
                    href={`/solo?game=${room.game.id}`}
                    target="_blank"
                    rel="noopener"
                    onClick={() => {
                      track("solo_cta", { action: "from_room", game_id: room.game?.id ?? null });
                    }}
                  >
                    Play solo while you wait
                  </a>
                </div>
              )}
            <ChatCard connection={connection} room={room} />
            {leaving.value === null ? (
              <button type="button" class="secondary" onClick={askLeave}>
                Leave room
              </button>
            ) : (
              <div class="card leave-choice" role="group" aria-label="Leave the room">
                <h2>Leave the room?</h2>
                {moveFull.value ? (
                  <HostingLimit hosting={moveFull.value} roomId={roomId} back={`/r/${roomId}`} />
                ) : (
                  <p class="hint">
                    Make a new room and everyone here is asked along, or leave on your own.
                  </p>
                )}
                {moveError.value && <p role="alert">{moveError.value}</p>}
                {!moveFull.value && (
                  <button
                    type="button"
                    disabled={leaving.value === "moving"}
                    onClick={() => void bringEveryone()}
                  >
                    {leaving.value === "moving" ? "Making the room…" : "New room, bring everyone"}
                  </button>
                )}
                <button type="button" class="secondary" onClick={leaveRoom}>
                  Just leave
                </button>
                <button
                  type="button"
                  class="secondary"
                  onClick={() => {
                    leaving.value = null;
                    moveFull.value = null;
                    moveError.value = null;
                  }}
                >
                  Stay
                </button>
              </div>
            )}
          </div>
          <MatchArea
            connection={connection}
            room={room}
            isHost={isHost}
            shown={inMatch}
            voice={voice}
            onLeave={leaveRoom}
            onBackToRoom={() => {
              backInRoom.value = load;
              const before = reachedAtPlay.current ?? [];
              fresh.value = (room.reached ?? []).filter((id) => !before.includes(id));
              reachedAtPlay.current = null;
            }}
          />
        </>
      )}
    </section>
  );
}

/** A room you're already in (P2-WEB-01): straight to it. */
function MemberEntry({ roomId }: { roomId: string }) {
  const connection = useMemo(() => new RoomConnection(roomId), [roomId]);
  // Back from Stripe's checkout (ADR 0022): after buying the Pass, wait for it to land (the
  // webhook can trail the buyer by a moment), so the room's token carries what it unlocks.
  const returned = useMemo(() => passReturn(location.search), [roomId]);
  const unlocking = useSignal(returned === "done");
  const notice = useSignal<string | null>(
    returned === "cancelled" ? "Checkout cancelled: nothing was charged." : null,
  );
  useEffect(() => {
    if (returned) history.replaceState(history.state, "", location.pathname + location.hash);
    const life = { gone: false };
    void (async () => {
      if (returned === "done") {
        const owned = await untilOwned(async () => (await api.me()).pass != null);
        if (life.gone) return;
        unlocking.value = false;
        notice.value = owned
          ? "Welcome to Obean Pass! Host as much as you like, and the Pass missions are open for everyone here."
          : "Your Pass is still being confirmed. Reload the room in a minute.";
      }
      if (!life.gone) void connection.open();
    })();
    return () => {
      life.gone = true;
      connection.close();
    };
  }, [connection]);
  if (unlocking.value)
    return (
      <section class="room">
        <p role="status">Confirming your Pass…</p>
      </section>
    );
  return (
    <>
      {notice.value && (
        <p role="status" class="pack-note">
          {notice.value}
        </p>
      )}
      <RoomView roomId={roomId} connection={connection} />
    </>
  );
}

/** `/r/:id`, plus `#secret` when it's an invite link (P2-WEB-02). */
export function RoomPage({ roomId }: { roomId: string }) {
  const secret = useMemo(() => secretFromHash(location.hash), [roomId]);
  return secret ? <InviteEntry roomId={roomId} secret={secret} /> : <MemberEntry roomId={roomId} />;
}

/**
 * Joining from an invite link (P2-WEB-02): what the link leads to, then just a name. Every way it
 * can fail is said plainly and reported as join_failed (docs/metrics.md).
 */
function InviteEntry({ roomId, secret }: { roomId: string; secret: string }) {
  const phase = useSignal<"checking" | "form" | "problem">("checking");
  const inviter = useSignal<string | null>(null);
  const problem = useSignal("");
  const name = useSignal("");
  const nameError = useSignal<string | null>(null);
  const busy = useSignal(false);
  const connection = useSignal<RoomConnection | null>(null);

  const fail = (stage: JoinStage, code: string) => {
    track("join_failed", { stage, code, ms_since_link_open: Math.round(performance.now()) });
    diagMark(`join_failed:${stage}:${code}`);
  };

  useEffect(() => {
    void (async () => {
      try {
        const guest = await api.guest();
        if (name.value === "") name.value = guest.nickname ?? "";
        const resolved = await api.resolveInvite(roomId, secret);
        inviter.value = resolved.inviter.nickname;
        if (resolved.state === "valid") {
          phase.value = "form";
          // Following a friend into their new room (#616): straight in, with the name we have.
          if (takeFollow(roomId) && name.value.trim() !== "") void join();
          return;
        }
        const why = inviteProblem(resolved.state, resolved.inviter.nickname);
        problem.value = why.message;
        phase.value = "problem";
        fail("link", why.code);
      } catch (err) {
        const notFound = err instanceof ApiError && err.status === 404;
        const tooMany = err instanceof ApiError && err.status === 429;
        problem.value = notFound
          ? inviteProblem("not_found", null).message
          : tooMany
            ? TOO_MANY_TRIES
            : "Can't reach Obean right now. Try again in a moment.";
        phase.value = "problem";
        fail("link", notFound ? "not_found" : tooMany ? "rate_limited" : "network");
      }
    })();
  }, [roomId, secret]);

  // Report a join that failed on the way in, and drop the secret from the address once joined.
  const status = connection.value?.status.value;
  useEffect(() => {
    if (status?.kind === "connected") history.replaceState(null, "", location.pathname);
    if (status?.kind !== "closed") return;
    const failure = joinFailure(status);
    if (failure) fail(failure.stage, failure.code);
  }, [status?.kind]);

  const join = async () => {
    busy.value = true;
    nameError.value = null;
    try {
      await api.setNickname(name.value);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "network";
      nameError.value = NICKNAME_ERRORS[code] ?? "Something went wrong. Try again.";
      fail("nickname", code);
      busy.value = false;
      return;
    }
    const next = new RoomConnection(roomId, secret);
    connection.value = next;
    void next.open();
  };

  if (connection.value) return <RoomView roomId={roomId} connection={connection.value} />;
  return (
    <section class="room">
      {phase.value === "checking" && <p role="status">Opening the invite…</p>}
      {phase.value === "problem" && (
        <div class="card">
          <p role="alert">{problem.value}</p>
          <button
            type="button"
            onClick={() => {
              navigate("/new");
            }}
          >
            Start your own room
          </button>
          <TellUs />
        </div>
      )}
      {phase.value === "form" && (
        <form
          class="card"
          onSubmit={(e) => {
            e.preventDefault();
            void join();
          }}
        >
          <h1>
            {inviter.value ? `${inviter.value} invited you to play` : "You're invited to play"}
          </h1>
          <p class="hint">
            Games and quizzes you play together, a few minutes a round, right here in your browser:
            no app, no account. Pick a name and you're in.
          </p>
          <label for="name">Your name</label>
          <input
            id="name"
            value={name.value}
            onInput={(e) => {
              name.value = e.currentTarget.value;
            }}
            maxLength={20}
            autocomplete="nickname"
            required
          />
          {nameError.value && <p role="alert">{nameError.value}</p>}
          <button type="submit" disabled={busy.value}>
            Join
          </button>
        </form>
      )}
    </section>
  );
}
